import { TreeNodeData, TreeView, toggleIsExpanded } from 'baseui/tree-view';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { filteredAppPermissionState, validatedAppPermissionState } from '../../../storage/permissions';
import { Permissions } from '../../../types/permissions';
import { usePermissionsTree } from './hooks/usePermissionsTree';

type Props = {
  userPermissions: Permissions;
  userId?: string;
  disabled?: boolean;
  onChangePermissions?: (newPermissions: Permissions) => void;
  useFilteredPermission?: boolean;
};
export function PermissionTree({
  userPermissions,
  onChangePermissions,
  disabled = true,
  userId,
  useFilteredPermission = false,
}: Readonly<Props>) {
  const appPermission = useRecoilValue(validatedAppPermissionState);
  const filteredAppPermission = useRecoilValue(filteredAppPermissionState);
  const { getPermissionTreeData } = usePermissionsTree(useFilteredPermission);
  const [data, setData] = useState<TreeNodeData[]>();

  useEffect(() => {
    if (!appPermission || !filteredAppPermission) return;
    const newData = [
      getPermissionTreeData({
        permission: useFilteredPermission ? filteredAppPermission : appPermission,
        userPermissions,
        disabled,
        onChangePermissions,
      }),
    ];
    if (data) {
      newData[0].isExpanded = data[0].isExpanded;
      newData[0].children = newData[0].children?.map((treeNode, index) => ({
        ...treeNode,
        isExpanded: data[0].children?.[index].isExpanded,
      }));
    }
    setData(newData);
  }, [userPermissions, useFilteredPermission]);

  useUpdateEffect(() => {
    setData((prevData) => {
      if (!prevData) return undefined;
      const newData = [...prevData];
      newData[0].isExpanded = false;
      newData[0].children = newData[0].children?.map((treeNode, index) => ({ ...treeNode, isExpanded: false }));
      return newData;
    });
  }, [userId]);

  if (!data) return null;

  return (
    <TreeView
      data={data}
      indentGuides
      onToggle={(node) => setData((prevData) => toggleIsExpanded(prevData!, node))}
    />
  );
}
